import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Page from '../../../components/Page';
import client from '../../../lib/feathers';
import EmailEditor from '../../../components/EmailEditor';
import TemplatePicker from '../../../components/TemplatePicker';

// Single compose page that replaces /admin/broadcasts/new and
// /admin/outreach/campaigns/new. Three audience tabs:
//   - By notification pref  (sends to opted-in users)
//   - By listing segment    (cold outreach contacts)
//   - Mixed                 (union, deduped by lower(email))
//
// Routing on send:
//   - by-pref     -> admin/user-broadcast
//   - by-segment  -> creates an outreach-campaign + .send() in one call
//                    (existing flow). The segment shape matches what
//                    the old /admin/outreach/campaigns/new produces.
//   - mixed       -> admin/campaign-mixed-send (Phase 4b)

const EVENT_OPTIONS = [
  { value: 'app_updates', label: 'App updates & new features' },
  { value: 'vinoseeker_news', label: 'News from VinoSeeker' },
];

const TIER_OPTIONS = [
  { value: '', label: 'Everyone' },
  { value: 'taster', label: 'Taster or higher' },
  { value: 'collector', label: 'Collector or higher' },
  { value: 'enthusiast', label: 'Enthusiast or higher' },
  { value: 'connoisseur', label: 'Connoisseur or higher' },
  { value: 'vip', label: 'VIP only' },
];

const TABS = [
  { key: 'by-pref', label: 'By notification pref' },
  { key: 'by-segment', label: 'By listing segment' },
  { key: 'mixed', label: 'Mixed' },
];

export default function NewCampaign() {
  const navigate = useNavigate();

  const initialAudience = (() => {
    if (typeof window === 'undefined') return 'by-pref';
    const p = new URLSearchParams(window.location.search).get('audience');
    return TABS.some((t) => t.key === p) ? p : 'by-pref';
  })();

  const [tab, setTab] = useState(initialAudience);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [variables, setVariables] = useState([]);

  useEffect(() => {
    let cancelled = false;
    client.service('admin/email-variable-catalog').find({})
      .then((r) => { if (!cancelled) setVariables(r.data || []); })
      .catch(() => { /* non-fatal: editor still works without the menu */ });
    return () => { cancelled = true; };
  }, []);

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setSubject(tpl.subject || '');
    setBodyHtml(tpl.bodyHtml || '');
  };

  // By-pref state
  const [prefEventKey, setPrefEventKey] = useState('vinoseeker_news');
  const [prefMinTier, setPrefMinTier] = useState('');
  const [prefCount, setPrefCount] = useState(null);
  const [prefCountError, setPrefCountError] = useState(null);

  // By-segment state (minimal shape — matches outreach-campaign segment)
  const [segClaimed, setSegClaimed] = useState('any'); // 'any' | 'true' | 'false'
  const [segRegion, setSegRegion] = useState('');
  const [segHasWebsite, setSegHasWebsite] = useState(false);
  const [segHasCheckins, setSegHasCheckins] = useState(false);
  const [segHasWines, setSegHasWines] = useState(false);
  const [segCount, setSegCount] = useState(null);
  const [segCountError, setSegCountError] = useState(null);

  const buildSegment = useCallback(() => ({
    ...(segClaimed === 'true' ? { claimed: true } : segClaimed === 'false' ? { claimed: false } : {}),
    ...(segRegion ? { region: segRegion } : {}),
    ...(segHasWebsite ? { hasWebsite: true } : {}),
    ...(segHasCheckins ? { hasCheckins: true } : {}),
    ...(segHasWines ? { hasWines: true } : {}),
  }), [segClaimed, segRegion, segHasWebsite, segHasCheckins, segHasWines]);

  // ── Live recipient counts ─────────────────────────────────────
  // by-pref count
  useEffect(() => {
    if (tab !== 'by-pref' && tab !== 'mixed') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await client.service('admin/user-broadcast-preview').find({
          query: { eventKey: prefEventKey },
        });
        if (!cancelled) {
          setPrefCount(res.count);
          setPrefCountError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setPrefCount(null);
          setPrefCountError(err.message || 'Could not load count');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tab, prefEventKey]);

  // by-segment count (only when relevant)
  useEffect(() => {
    if (tab !== 'by-segment' && tab !== 'mixed') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await client.service('admin/campaign-segment-preview').create({
          segment: buildSegment(),
        });
        if (!cancelled) {
          setSegCount(res.count);
          setSegCountError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setSegCount(null);
          setSegCountError(err.message || 'Could not load count');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tab, buildSegment]);

  const handleSend = async () => {
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error('Subject and body are required.');
      return;
    }
    const confirmed = window.confirm(
      `Send "${subject}" — this cannot be undone.`,
    );
    if (!confirmed) return;
    setSending(true);
    try {
      if (tab === 'by-pref') {
        await client.service('admin/user-broadcast').create({
          eventKey: prefEventKey,
          subject: subject.trim(),
          bodyHtml,
        });
      } else if (tab === 'by-segment') {
        // Create outreach-campaign draft, then call .send.
        const created = await client.service('admin/outreach-campaign').create({
          name: subject.trim(),
          subject: subject.trim(),
          type: 'custom',
          segment: buildSegment(),
          fromEmail: 'service@vinoseeker.com',
          fromName: 'VinoSeeker',
          // outreach-campaign uses templateId (string alias). For inline
          // body sends from this UI we route through the mixed-send
          // endpoint instead, which supports bodyHtml. So this branch
          // requires the operator to have a Postmark template configured;
          // we surface a clear error if not.
          templateId: 'broadcast-2026',
        });
        await client.service('admin/outreach-campaign').patch(created.id, {
          action: 'send',
        }).catch(async () => {
          // older shape uses .send(id) via a custom verb; fall back to
          // hitting the campaign's send endpoint directly.
          await fetch(`/admin/outreach-campaign/${created.id}/send`, { method: 'POST' });
        });
      } else if (tab === 'mixed') {
        await client.service('admin/campaign-mixed-send').create({
          userAudience: { eventKey: prefEventKey, minTier: prefMinTier || undefined },
          listingAudience: { segment: buildSegment() },
          subject: subject.trim(),
          bodyHtml,
        });
      }
      toast.success('Campaign sent.');
      navigate('/admin/campaigns');
    } catch (err) {
      toast.error(err.message || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  const liveCount = (() => {
    if (tab === 'by-pref') return prefCount;
    if (tab === 'by-segment') return segCount;
    if (tab === 'mixed') {
      if (prefCount === null || segCount === null) return null;
      // Best-effort estimate before the mixed endpoint resolves the
      // actual deduped count. Mostly informational.
      return prefCount + segCount;
    }
    return null;
  })();

  return (
    <Page title="New Campaign">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold mb-2">Compose Campaign</h1>
        <p className="text-sm text-gray-600 mb-6">
          Picks the audience, writes the email, fires through Postmark with full open / click tracking.
          The unsubscribe footer adapts: user-linked recipients land on
          <code className="mx-1 px-1 bg-gray-100 rounded">/me/notifications</code>; cold contacts get the public
          <code className="mx-1 px-1 bg-gray-100 rounded">/outreach-unsubscribe</code> page.
        </p>

        <div className="bg-white shadow rounded-lg">
          {/* Tab bar */}
          <div className="border-b border-gray-200 flex">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 ${
                  tab === t.key
                    ? 'border-red-700 text-red-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-6 space-y-6">
            {/* By-pref controls */}
            {(tab === 'by-pref' || tab === 'mixed') && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-gray-700">Users audience</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notification preference category</label>
                  <select
                    value={prefEventKey}
                    onChange={(e) => setPrefEventKey(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                  >
                    {EVENT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier filter</label>
                  <select
                    value={prefMinTier}
                    onChange={(e) => setPrefMinTier(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                  >
                    {TIER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="px-3 py-2 rounded bg-blue-50 text-sm text-blue-900">
                  {prefCountError && <span className="text-red-700">{prefCountError}</span>}
                  {!prefCountError && prefCount === null && 'Counting users…'}
                  {!prefCountError && prefCount !== null && (
                    <span><strong>{prefCount.toLocaleString()}</strong> users opted in to this category.</span>
                  )}
                </div>
              </div>
            )}

            {/* By-segment controls */}
            {(tab === 'by-segment' || tab === 'mixed') && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-gray-700">Listings audience</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Claim status</label>
                  <select
                    value={segClaimed}
                    onChange={(e) => setSegClaimed(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="any">Any</option>
                    <option value="true">Claimed only</option>
                    <option value="false">Unclaimed only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Region (state or city)</label>
                  <input
                    type="text"
                    value={segRegion}
                    onChange={(e) => setSegRegion(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="e.g. OR or Portland"
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="inline-flex items-center text-sm">
                    <input type="checkbox" checked={segHasWebsite} onChange={(e) => setSegHasWebsite(e.target.checked)} className="mr-2" />
                    Has a website
                  </label>
                  <label className="inline-flex items-center text-sm">
                    <input type="checkbox" checked={segHasCheckins} onChange={(e) => setSegHasCheckins(e.target.checked)} className="mr-2" />
                    Has at least one check-in
                  </label>
                  <label className="inline-flex items-center text-sm">
                    <input type="checkbox" checked={segHasWines} onChange={(e) => setSegHasWines(e.target.checked)} className="mr-2" />
                    Has wines in catalog
                  </label>
                </div>
                <div className="px-3 py-2 rounded bg-blue-50 text-sm text-blue-900">
                  {segCountError && <span className="text-red-700">{segCountError}</span>}
                  {!segCountError && segCount === null && 'Counting contacts…'}
                  {!segCountError && segCount !== null && (
                    <span><strong>{segCount.toLocaleString()}</strong> listing contacts match.</span>
                  )}
                </div>
              </div>
            )}

            {tab === 'mixed' && (
              <div className="px-3 py-2 rounded bg-yellow-50 text-sm text-yellow-900">
                Mixed audience will send to the union of both pools, deduped by email (case-insensitive).
                The same person on both lists gets one email.
              </div>
            )}

            <hr />

            <TemplatePicker onPick={applyTemplate} />

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                placeholder="A heads-up about your library — Hi {{firstName}}"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {'{{firstName}} and other variables work here too.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
              <EmailEditor
                value={bodyHtml}
                onChange={setBodyHtml}
                variables={variables}
                placeholder="Write your message…"
                minHeight="320px"
              />
              <p className="text-xs text-gray-500 mt-1">
                Unsubscribe footer is auto-appended per recipient. Use the
                <strong> + Variable</strong> button to insert {'{{firstName}}'}-style tokens that the server fills in on send.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/admin/campaigns')}
                disabled={sending}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || liveCount === 0}
                className="px-4 py-2 text-sm text-white bg-red-700 rounded-md hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending
                  ? 'Sending…'
                  : `Send${liveCount !== null ? ` to ~${liveCount.toLocaleString()} ` : ' '}recipients`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}
